import { Component, ElementRef, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { CommonModule } from "@angular/common";
import { IonicModule, ModalOptions } from "@ionic/angular";
import { ModalPensionHistoryComponent } from "../modals/modal-pension-history/modal-pension-history.component";
import { ModalService } from "@services/modal/modal.service";
import { TramitePensionService } from "../../services/tramite-pension.service";
import { StorageService } from "@services/storage/storage.service";
import { TramiteStepperService } from "../../services/tramite-stepper.service";
import { Util } from "@shared/common/utils/util";
import {
  CODIGO_PARENTESTO_HIJO_O_HIJA,
  HeadersTableBalances,
  HeadersTableBeneficiaries,
  STORAGE_FORMULARIO_DATOS_PERSONALES,
} from "@constants/tramite-pension-constant";
import {
  ROUTING_CONGELAMIENTOS_SALDOS_PENSION,
  ROUTING_TRAMITE_PENSION,
} from "@constants/url.constant";
import { ACCOUNTS, storageTramitePension } from "./personal-data.const";
import { firstValueFrom } from "rxjs/internal/firstValueFrom";
import { TipoMensajeSalidaFlujoTramitePension } from "@constants/business.constant";
import { DropdownlistComponent } from "../../components/dropdownlist/dropdownlist.component";
import { BotonVolverComponent } from "../../components/boton-volver/boton-volver.component";
import { SwipeRightIconComponent } from "src/assets/tramite-pension/icons/svg/swipe_right/swipe_right.component";
import { GtmService } from "@services/gtm/gtm.service";

@Component({
  selector: "app-personal-data",
  templateUrl: "./personal-data.component.html",
  styleUrls: ["./personal-data.component.scss"],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IonicModule, DropdownlistComponent, BotonVolverComponent, SwipeRightIconComponent]
})
export class PersonalDataComponent implements OnInit, OnDestroy {
  private requestLayoutHost: HTMLElement | null = null;
  private readonly ACCOUNT_IDS_OBLIGATORIO = ['CCO', 'CAF', 'CAI', 'CDC'];
  private readonly ACCOUNT_IDS_VOLUNTARIO = ['CAV', 'CCV', 'CVC'];

  personalData: any;
  headerBalances = HeadersTableBalances;
  balances: any[] = [];
  headerBeneficiaries = HeadersTableBeneficiaries;
  beneficiaries: any[] = [];
  btnDisabled = true;
  form: FormGroup;
  showErrorEncuesta: boolean = false;
  loadingPersonalData = true;
  dataDropdown: any;
  valueDropdown: number = 0;
  bonos: any[] = [];
  balancesVoluntario: any[] = [];
  aporteEstatalRows: Array<{
    fund: string;
    balanceInstallments: string;
    balancePesos: string;
  }> = [];
  readonly headerAporteEstatal = ["Fondo", "Saldo Cuotas", "Saldo Pesos*"];
  readonly textoAporteEstatalApv =
    "El Estado te bonifica con un 15% del monto total ahorrado y se destina completamente a tu pensión";

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly activatedRoute: ActivatedRoute,
    readonly router: Router,
    private readonly util: Util,
    private readonly _modalService: ModalService,
    private readonly tramitePension: TramitePensionService,
    private readonly storageService: StorageService,
    private readonly tramiteStepper: TramiteStepperService,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly gtmService: GtmService
  ) {
    this.createForm();
  }

  ngOnDestroy(): void {
    this.restoreRequestLayoutHost();
  }

  ionViewWillLeave(): void {
    this.restoreRequestLayoutHost();
  }

  ionViewWillEnter(): void {
    this.applyRequestSingleScrollLayout();
  }

  async ngOnInit() {
    this.applyRequestSingleScrollLayout();
    await this.getPersonalData();
    this.getBeneficiarios();
    await this.fillBalanceTable();
    this.formValueChange();
    this.tramiteStepper.setStepCurrent(2);
    this.obtenerFormularioDatosPesonalesStorage();
    await this.getBonos();
    await this.fillBalanceTableVoluntario();
    await this.fillAporteEstatalApv();
    const isPensionHistoryForm =
      sessionStorage.getItem("isPensionHistoryForm") ?? "0";
    if (Number(isPensionHistoryForm))
      this.form.patchValue({ checkModal: true });
    await this.setDropdownInstitucionesSalud();
  }
  async setDropdownInstitucionesSalud() {
    this.dataDropdown = await this.tramitePension.getInstitucionesDeSalud();

    this.dataDropdown.sort((a: { name: string; }, b: { name: string; }) => {
      const isAFonasa = a.name.toLowerCase() === 'fonasa';
      const isBFonasa = b.name.toLowerCase() === 'fonasa';

      if (isAFonasa) return -1;
      if (isBFonasa) return 1;
      return 0;
    });

    this.valueDropdown = Number(sessionStorage.getItem(storageTramitePension.institucionSalud)) || 0;
  }

  createForm() {
    this.form = this.formBuilder.group({
      checkData: [false],
      checkDeclaracionSolteria: [false],
      checkModal: [false],
      indicadorDatosPersonalesPublico: [null],
    });
  }

  get tieneBeneficiariosHijos(): boolean {
    return this.beneficiaries.some(
      (beneficiary) =>
        beneficiary.codigoParentesco === CODIGO_PARENTESTO_HIJO_O_HIJA
    );
  }

  private obtenerFormularioDatosPesonalesStorage() {
    let formulario = sessionStorage.getItem(
      STORAGE_FORMULARIO_DATOS_PERSONALES
    );
    const formularioJSON = formulario ? JSON.parse(formulario) : {};
    this.form.patchValue({
      indicadorDatosPersonalesPublico:
        formularioJSON?.indicadorDatosPersonalesPublico,
      checkDeclaracionSolteria: formularioJSON?.checkDeclaracionSolteria ?? false,
    });
  }

  esValidoindicadorDatosPersonalesPublico(): boolean {
    return this.form?.value?.indicadorDatosPersonalesPublico === true || this.form?.value?.indicadorDatosPersonalesPublico === false;
  }

  formValueChange() {
    const value = this.form.value;
    const solteriaConfirmada =
      !this.tieneBeneficiariosHijos || value.checkDeclaracionSolteria;

    if (value.checkData && solteriaConfirmada && this.isValidInstitucionSalud()) {
      this.btnDisabled = false;
    } else {
      this.btnDisabled = true;
    }
  }

  goBack() {
    this.router.navigate([`../${ROUTING_TRAMITE_PENSION.SEGURIDAD}`], {
      relativeTo: this.activatedRoute,
    });
  }

  async getPersonalData() {
    const rut = await this.tramitePension.uid();
    try {
      const response = await firstValueFrom(this.tramitePension.getPersonalData());
      const date = response?.fechaNacimiento.split("-");
      this.personalData = {
        name: this.util.capitalizeText(response?.nombres),
        lastName: `${this.util.capitalizeText(response?.apellidoPaterno)} ${this.util.capitalizeText(response?.apellidoMaterno)}`,
        rut: this.stringToFormattedRut(rut),
        gender: this.util.capitalizeText(response?.sexo),
        birthDate: `${date[0]}/${date[1]}/${date[2]}`,
        civilStatus: this.util.capitalizeText(response?.estadoCivil),
        address: this.util.capitalizeText(this.validAddress(
          response.direccion,
          response.ciudad,
          response.paisResidencia
        ) || ""),
        phoneNumber: response?.telMovil,
        email: response?.email,
      };
    } catch (error) {
      console.error("Error al obtener el servicio", error);
    }
    this.loadingPersonalData = false;
  }

  validAddress(direccion: string, ciudad: string, pais: string): string | null {
    if (
      direccion === undefined ||
      direccion.trim() === "" ||
      ciudad === undefined ||
      ciudad.trim() === "" ||
      pais === undefined ||
      pais.trim() === ""
    ) {
      return null;
    }
    return `${direccion}, ${ciudad}, ${pais}`;
  }

  getBeneficiarios() {
    this.tramitePension.getBeneficiaries().subscribe(
      (response) => {
        this.beneficiaries =
          response?.map((item) => ({
            nombre: `${item.apellidoPaterno} ${item.apellidoMaterno}, ${item.nombres}`,
            parentesco: item.parentesco,
            codigoParentesco: item.codigoParentesco,
            rut: this.stringToFormattedRut(item.idBeneficiario),
          })) ?? [];
        this.formValueChange();
      },
      (error) => {
        console.error("Error al obtener el servicio", error);
        this.beneficiaries = [];
        this.formValueChange();
      }
    );
  }

  stringToFormattedRut = (rut: string): string => {
    const removeInitialCeros = Number(rut.slice(0, -1));
    const rutString = removeInitialCeros.toString();
    const rutWithDots = this.formatThousandsWithDots(rutString);
    const rutWithDotsAndDash = `${rutWithDots}-${rut.slice(-1)}`;
    return rutWithDotsAndDash;
  };

  private formatThousandsWithDots(value: string): string {
    if (value.length <= 3) {
      return value;
    }

    const remainder = value.length % 3;
    const prefix = remainder ? `${value.slice(0, remainder)}.` : '';
    const groups = value.slice(remainder).match(/\d{3}/g)?.join('.') ?? '';
    return `${prefix}${groups}`;
  }

  goToYourData() {
    this.gtmService.trackClick('button', 'Tus datos', 'link_interno', 'modal');
    void this._modalService.openGenericModalMensajeSalida(TipoMensajeSalidaFlujoTramitePension.ActualizaDatos);
  }

  goToYourBeneficiaries() {
    this.gtmService.trackClick('button', 'Actualizar tus beneficiarios', 'link_interno', 'modal');
    void this._modalService.openGenericModalMensajeSalida(TipoMensajeSalidaFlujoTramitePension.Beneficiario);
  }

  goToVideoAtencion() {
    this.gtmService.trackClick('button', 'Video atención', 'link_interno', 'modal');
    void this._modalService.openGenericModalMensajeSalida(TipoMensajeSalidaFlujoTramitePension.VideoAtencion);
  }

  goToFundChange() {
    this.gtmService.trackClick('button', 'Cambio de Fondos', 'link_interno', 'modal');
    void this._modalService.openGenericModalMensajeSalida(TipoMensajeSalidaFlujoTramitePension.CambioFondo);
  }

  goToFreezeBalance() {
    this.router.navigate([
      "/",
      ROUTING_CONGELAMIENTOS_SALDOS_PENSION.COGELAMIENTO_SALDOS_BASE,
      ROUTING_CONGELAMIENTOS_SALDOS_PENSION.COGELAMIENTO_SALDOS_URL,
      ROUTING_CONGELAMIENTOS_SALDOS_PENSION.COGELAMIENTO_SALDOS_HOME,
    ]);
  }

  async fillBalanceTable() {
    const cuentas = await this.storageService.get("accounts");
    this.balances = this.processBalances(cuentas, this.ACCOUNT_IDS_OBLIGATORIO);
  }

  private processBalances(cuentas: any[], allowedAccountIds: string[]) {
    if (!cuentas) return [];

    return cuentas
      .filter((balance) =>
        allowedAccountIds.includes(this.getAccountId(balance))
      )
      .map((balance) => {
        const accountId = this.getAccountId(balance);
        const funds = balance.funds ?? [];
        const rows =
          funds.length > 0
            ? funds.map((fund: any) => ({
                fund: `Fondo ${fund.id}`,
                balanceInstallments: this.formatSaldoCuotas(fund.shareBalance),
                balancePesos: this.util.currencyFormat(fund.amount ?? 0),
              }))
            : [
                {
                  fund: "—",
                  balanceInstallments: "—",
                  balancePesos: this.util.currencyFormat(balance.amount ?? 0),
                },
              ];

        return {
          accountType:
            balance.accountConfiguration?.name ??
            this.getAccountTitle(accountId) ??
            accountId,
          rows,
        };
      });
  }

  private getAccountId(balance: any): string {
    return balance.accountConfiguration?.id ?? balance.id ?? "";
  }

  private formatSaldoCuotas(shareBalance: number | string | undefined): string {
    if (shareBalance === null || shareBalance === undefined || shareBalance === "") {
      return "—";
    }
    if (typeof shareBalance === "number") {
      return this.util.currencyFormat(shareBalance);
    }
    return String(shareBalance);
  }

  getAccountTitle(id: string) {
    return ACCOUNTS.find((acc) => acc.value === id)?.title;
  }
  async openModalPensionHistoryCheck(): Promise<void> {
    const isPensionHistoryForm =
      sessionStorage.getItem("isPensionHistoryForm") ?? "0";
    if (this.form.value.checkModal && Number(isPensionHistoryForm)) return;

    await this.openModalPensionHistory();
  }

  async openModalPensionHistory() {
    this.showErrorEncuesta = false;
    const isPensionHistoryForm =
      sessionStorage.getItem("isPensionHistoryForm") ?? "0";

    const info = {
      title: "Antecedentes de pensión",
      closeButton: () => this._modalService.closeModal(),
    };

    const opcionesModal: ModalOptions = {
      component: ModalPensionHistoryComponent,
      backdropDismiss: true,
      cssClass: "pension-modal",
      componentProps: { data: info },
    };
    this.form.patchValue({ checkModal: false });
    const modal = await this._modalService.openModal(opcionesModal);
    const { data } = await modal.onDidDismiss();

    if (data?.isFormValid && Number(isPensionHistoryForm)) {
      this.form.patchValue({ checkModal: true });
    }
    if (data?.isFormValid && !Number(isPensionHistoryForm)) {
      sessionStorage.setItem("isPensionHistoryForm", "1");
      this.form.patchValue({ checkModal: true });
    }
    if (!data?.isFormValid && Number(isPensionHistoryForm)) {
      this.form.patchValue({ checkModal: true });
    }
    if (!data?.isFormValid && !Number(isPensionHistoryForm)) {
      sessionStorage.setItem("isPensionHistoryForm", "0");
      this.form.patchValue({ checkModal: false });
      this.showErrorEncuesta = true;
    }
  }

  async continue() {
    try {
      const formData = this.form.value;
      sessionStorage.setItem(STORAGE_FORMULARIO_DATOS_PERSONALES, JSON.stringify(formData));
      this.gtmService.trackClick('button', 'Continuar', 'boton_principal', '/pensions/tramite-pensiones/solicitud/antecedentes-pension');
      await this.router.navigate([`../${ROUTING_TRAMITE_PENSION.ANTECEDENTES_PENSION}`], {
        relativeTo: this.activatedRoute,
      });
    } catch (error) {
      console.error("Error al navegar:", error);
    }
  }
  onChangeDropdown(event: any) {
    this.valueDropdown = event;
    sessionStorage.setItem(storageTramitePension.institucionSalud, event);
    this.formValueChange();
  }
  isValidInstitucionSalud(): boolean {
    return this.valueDropdown !== 0;
  }

  async getBonos() {
    try {
      const response =
        (await this.storageService.get("recognition_bonds")) ?? [];
      this.bonos = response.map((bono: any) => ({
        ...bono,
        currentValue: this.util.currencyFormat(bono.currentValue),
        bondTypeDescription: this.util.capitalizeText(bono.bondTypeDescription),
        bondStatus: this.util.capitalizeText(bono.bondStatus),
        expirationDate: this.util.formatYYYYMMDDtoDDMMYYYY(bono.updateDate),
      }));
    } catch (error) {
      console.error("Error al obtener bonos", error);
      this.bonos = [];
    }
  }

  async fillBalanceTableVoluntario() {
    const cuentas: any[] = (await this.storageService.get("accounts")) ?? [];
    const cuentasVoluntarias = cuentas.filter((cuenta) =>
      this.ACCOUNT_IDS_VOLUNTARIO.includes(this.getAccountId(cuenta))
    );
    const usaSubBalances = cuentasVoluntarias.some((cuenta) =>
      cuenta.funds?.some(
        (fund: { subBalances?: unknown[] }) =>
          Array.isArray(fund.subBalances) && fund.subBalances.length > 0
      )
    );

    this.balancesVoluntario = usaSubBalances
      ? this.processBalancesVoluntariosConSubBalances(cuentasVoluntarias)
      : this.processBalances(cuentas, this.ACCOUNT_IDS_VOLUNTARIO);
  }

  async fillAporteEstatalApv() {
    const cuentas: any[] = (await this.storageService.get("accounts")) ?? [];
    this.aporteEstatalRows = this.obtenerDetalleAporteEstatalApv(cuentas)
      .filter((item) => Number(item.saldoPesos) > 0)
      .map((item) => ({
        fund: `Fondo ${item.fondo}`,
        balanceInstallments: this.formatSaldoCuotas(item.saldoCuotas),
        balancePesos: this.util.currencyFormat(item.saldoPesos),
      }));
  }

  private processBalancesVoluntariosConSubBalances(cuentasVoluntarias: any[]) {
    return cuentasVoluntarias.flatMap((cuenta) =>
      this.buildVoluntarioBalanceGroups(cuenta)
    );
  }

  private buildVoluntarioBalanceGroups(cuenta: any) {
    const producto = this.getAccountId(cuenta);
    const filasPorTipo = new Map<
      string,
      Array<{
        fund: string;
        balanceInstallments: string;
        balancePesos: string;
      }>
    >();

    for (const fund of cuenta.funds ?? []) {
      for (const subBalance of fund.subBalances ?? []) {
        this.aggregateVoluntarioSubBalance(
          filasPorTipo,
          fund,
          subBalance,
          producto
        );
      }
    }

    return Array.from(filasPorTipo.entries()).map(([accountType, rows]) => ({
      accountType,
      rows,
    }));
  }

  private aggregateVoluntarioSubBalance(
    filasPorTipo: Map<
      string,
      Array<{
        fund: string;
        balanceInstallments: string;
        balancePesos: string;
      }>
    >,
    fund: any,
    subBalance: any,
    producto: string
  ): void {
    if (!this.shouldShowVoluntarioSubBalance(subBalance)) {
      return;
    }

    const accountType = this.util.glosaTipoCuenta(
      subBalance.category,
      subBalance.taxRegimeCode,
      producto,
      true
    );
    const row = {
      fund: `Fondo ${fund.id}`,
      balanceInstallments: this.formatSaldoCuotas(subBalance.shareBalance),
      balancePesos: this.util.currencyFormat(subBalance.clpBalance ?? 0),
    };

    const rows = filasPorTipo.get(accountType);
    if (rows) {
      rows.push(row);
      return;
    }

    filasPorTipo.set(accountType, [row]);
  }

  private shouldShowVoluntarioSubBalance(subBalance: any): boolean {
    return (
      subBalance.category !== "ESTADO" &&
      Number(subBalance.clpBalance ?? 0) > 0
    );
  }

  private obtenerDetalleAporteEstatalApv(cuentas: any[]): Array<{
    cuenta: string;
    fondo: string;
    categoria: string;
    codigoRegimen: string;
    saldoPesos: number;
    saldoCuotas: number | string;
  }> {
    const detalle: Array<{
      cuenta: string;
      fondo: string;
      categoria: string;
      codigoRegimen: string;
      saldoPesos: number;
      saldoCuotas: number | string;
    }> = [];

    for (const cuenta of cuentas) {
      const accountId = this.getAccountId(cuenta);

      for (const fund of cuenta.funds ?? []) {
        for (const subBalance of fund.subBalances ?? []) {
          const esAporteEstatal =
            subBalance.category === "ESTADO" &&
            (subBalance.taxRegimeCode === "A" ||
              subBalance.taxRegimeCode === "B");

          if (!esAporteEstatal) {
            continue;
          }

          detalle.push({
            cuenta: accountId,
            fondo: fund.id,
            categoria: subBalance.category,
            codigoRegimen: subBalance.taxRegimeCode,
            saldoPesos: Number(subBalance.clpBalance ?? 0),
            saldoCuotas: subBalance.shareBalance ?? "—",
          });
        }
      }
    }

    return detalle;
  }

  onCheckboxKeydown(event: KeyboardEvent, controlName = "checkData") {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      const currentValue = this.form.get(controlName)?.value;
      this.form.patchValue({ [controlName]: !currentValue });
      this.formValueChange();
    }
  }

  private applyRequestSingleScrollLayout(): void {
    this.requestLayoutHost = this.elementRef.nativeElement.closest(
      "app-tramite-pension-request"
    );
    if (!this.requestLayoutHost) {
      return;
    }
    this.requestLayoutHost.style.setProperty("display", "block");
    this.requestLayoutHost.style.setProperty("height", "100%");
    this.requestLayoutHost.style.setProperty("max-height", "100%");
    this.requestLayoutHost.style.setProperty("min-height", "0");
    this.requestLayoutHost.style.setProperty("overflow", "hidden");
  }

  private restoreRequestLayoutHost(): void {
    if (!this.requestLayoutHost) {
      return;
    }
    this.requestLayoutHost.style.removeProperty("display");
    this.requestLayoutHost.style.removeProperty("height");
    this.requestLayoutHost.style.removeProperty("max-height");
    this.requestLayoutHost.style.removeProperty("min-height");
    this.requestLayoutHost.style.removeProperty("overflow");
    this.requestLayoutHost = null;
  }
}
